import React, { useEffect, useState, useMemo } from "react";
import { Box, Container, Tabs, Tab, Typography } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import {
  ReportFilterProvider,
  useReportFilter,
} from "./context/ReportFilterContext";
import ReportFilter from "./components/ReportFilter";
import CommissionReport from "./components/CommissionReport";
import ExpenseReport from "./components/ExpenseReport";
import IncomeReport from "./components/IncomeReport";
import PNLReport from "./components/PNLReport";
import RentalJourneyPNLReport from "./components/RentalJourneyPNLReport";
import MaintenanceReport from "./components/MaintenanceReport";
import PickupReport from "./components/PickupReport";
import DriverCleanerReport from "./components/DriverCleanerReport";
import LedgerReport from "./components/LedgerReport";
import ReportByVehicles from "./components/ReportByVehicles";
import { usePlanStatus } from "../../hooks/usePlanStatus";
import { getPermissionFilteredReportTypes } from "./report";
import FuelOverallReport from "./components/FuelOverallReport";
import MaintenanceOverallReport from "./components/MaintenanceOverallReport";
import { OperationType } from "../../types/operationType";
import IncomeOverallReport from "./components/IncomeOverallReport";
import PNLOverallReport from "./components/PNLOverallReport";
import RentalLedgerReport from "./components/RentalLedgerReport";

interface TabPanelProps {
  children?: React.ReactNode;
  type: string;
  currentType?: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, type, currentType, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={type !== currentType}
      id={`report-tabpanel-${type}`}
      aria-labelledby={`report-tab-${type}`}
      {...other}
    >
      {type === currentType && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(type: string) {
  return {
    id: `report-tab-${type}`,
    "aria-controls": `report-tabpanel-${type}`,
  };
}

const ReportContent: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const { resetFilters } = useReportFilter();
  const { planStatus } = usePlanStatus();
  const reportTypes = getPermissionFilteredReportTypes(
    planStatus.company?.operationType,
    planStatus?.permissions
  );
  const [currentReportType, setCurrentReportType] = useState(type);

  // Find the current tab index based on the type
  const currentTabIndex = useMemo(() => {
    const index = reportTypes.findIndex(
      (reportType) => reportType.type === type
    );
    return index !== -1 ? index : 0; // Default to first tab if type not found
  }, [reportTypes, type]);

  useEffect(() => {
    // Reset filters when report type changes
    // resetFilters();
    // Reset expense tab index when switching report types
    setCurrentReportType(type);
  }, [type, resetFilters]);

  // Check if user has permission to access the current report type
  useEffect(() => {
    if (type && reportTypes.length > 0) {
      const hasAccess = reportTypes.some(
        (reportType) => reportType.type === type
      );
      if (!hasAccess) {
        // Redirect to first available report type or reports page
        const firstAvailableType = reportTypes[0]?.type;
        if (firstAvailableType) {
          navigate(`/reports/${firstAvailableType}`, { replace: true });
        } else {
          navigate("/reports", { replace: true });
        }
      }
    }
  }, [type, reportTypes, navigate]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const selectedType = reportTypes[newValue].type;
    setCurrentReportType(selectedType);
    navigate(`/reports/${selectedType}`);
  };

  const showFilters = () => {
    let filters = {
      showVehicleFilter: true,
      showLocationFilter: false,
      showOperatorFilter: false,
      showPickupManFilter: false,
      showMechanicFilter: false,
      showGarageFilter: false,
      showProviderFilter: false,
      showDriverCleanerFilter: false,
      showVehicleIdFilter: false,
      showProviderIdFilter: false,
      showProjectIdFilter: false,
      showPartyIdFilter: false,
    };

    switch (type) {
      case "commission":
        filters.showLocationFilter = true;
        filters.showOperatorFilter = true;
        break;
      case "pickup":
        filters.showLocationFilter = true;
        filters.showPickupManFilter = true;
        break;
      case "maintenance":
        filters.showMechanicFilter = true;
        filters.showGarageFilter = true;
        filters.showVehicleIdFilter = true;
        filters.showProjectIdFilter = true;
        filters.showVehicleFilter = false;
        break;
      case "fuel":
        filters.showVehicleIdFilter = true;
        filters.showProviderIdFilter = true;
        filters.showProjectIdFilter = true;
        filters.showVehicleFilter = false;
        break;
      case "rentalJourneyPnl":
      case "rentalJourneyLedger":
        filters.showVehicleIdFilter = true;
        filters.showPartyIdFilter = true;
        filters.showVehicleFilter = false;
        break;
      case "driverCleaner":
        filters.showDriverCleanerFilter = true;
        break;
      case "income":
        if (planStatus.company?.operationType === OperationType.LARGE_FLEET) {
          filters.showVehicleFilter = false;
        }
        break;
      case "expense":
        filters.showVehicleFilter = false;
        filters.showVehicleIdFilter = true;
        break;
      default:
        filters.showVehicleFilter = true;
        break;
    }
    return filters;
  };

  const filters = useMemo(() => showFilters(), [type]);

  return (
    <Container>
      <Box sx={{ py: 4 }}>
        {reportTypes.length > 0 ? (
          <>
            {/* Enhanced Tab Bar */}
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: 3,
                p: 1,
                px: 0,
                mb: 3,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                border: "1px solid #e0e0e0",
              }}
            >
              <Tabs
                value={currentTabIndex}
                onChange={handleTabChange}
                aria-label="report tabs"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1976d2",
                    height: 2,
                    borderRadius: 2,
                  },
                  "& .MuiTab-root": {
                    color: "rgba(0, 0, 0, 0.6)",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    minHeight: 64,
                    textTransform: "none",
                    borderRadius: 2,
                    margin: "0 4px",
                    padding: "12px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.04)",
                      color: "#1976d2",
                    },
                    "&.Mui-selected": {
                      color: "#1976d2",
                      backgroundColor: "rgba(25, 118, 210, 0.08)",
                      boxShadow: "0 2px 12px rgba(25, 118, 210, 0.15)",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: "1.2rem",
                      marginBottom: "2px",
                    },
                  },
                }}
              >
                {reportTypes.map((type) => (
                  <Tab
                    key={type.type}
                    icon={type.icon}
                    label={type.shortLabel}
                    {...a11yProps(type.type)}
                    sx={{
                      "&.Mui-selected": {
                        "& .MuiSvgIcon-root": {
                          color: "#2196F3",
                          filter:
                            "drop-shadow(0 0 8px rgba(255, 255, 255, 0.3))",
                        },
                      },
                    }}
                  />
                ))}
              </Tabs>
            </Box>

            <ReportFilter {...filters} />

            <TabPanel type="pnl" currentType={currentReportType}>
              {planStatus.company?.operationType ===
              OperationType.LARGE_FLEET ? (
                <PNLOverallReport />
              ) : (
                <PNLReport />
              )}
            </TabPanel>
            <TabPanel type="rentalJourneyPnl" currentType={currentReportType}>
              <RentalJourneyPNLReport />
            </TabPanel>
            <TabPanel type="ledger" currentType={currentReportType}>
              <LedgerReport />
            </TabPanel>
            <TabPanel
              type="rentalJourneyLedger"
              currentType={currentReportType}
            >
              <RentalLedgerReport />
            </TabPanel>
            <TabPanel type="income" currentType={currentReportType}>
              {planStatus.company?.operationType ===
              OperationType.LARGE_FLEET ? (
                <IncomeOverallReport />
              ) : (
                <IncomeReport />
              )}
            </TabPanel>
            <TabPanel type="expense" currentType={currentReportType}>
              <ExpenseReport />
            </TabPanel>
            <TabPanel type="fuel" currentType={currentReportType}>
              <FuelOverallReport />
            </TabPanel>
            <TabPanel type="maintenance" currentType={currentReportType}>
              {/* <MaintenanceReport /> */}
              <MaintenanceOverallReport />
            </TabPanel>
            <TabPanel type="commission" currentType={currentReportType}>
              <CommissionReport />
            </TabPanel>
            <TabPanel type="pickup" currentType={currentReportType}>
              <PickupReport />
            </TabPanel>
            <TabPanel type="driverCleaner" currentType={currentReportType}>
              <DriverCleanerReport />
            </TabPanel>
            <TabPanel type="reportByVehicles" currentType={currentReportType}>
              <ReportByVehicles />
            </TabPanel>
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              No Reports Available
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              You don't have permission to view any reports. Please contact your
              administrator for access.
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

const ReportByType: React.FC = () => {
  return (
    <ReportFilterProvider>
      <ReportContent />
    </ReportFilterProvider>
  );
};

export default ReportByType;
